/**
 * UsageMeter — compact monthly-usage readout for the Resume Workspace.
 *
 * Presentational only: it renders whatever `usage` it's handed and never
 * fetches. The parent (ResumeWorkspaceHome) owns the React Query call.
 *
 * Two facts surface here:
 *   - Runs this month, with a determinate progress bar (always shown).
 *   - Targets used / allowed — shown ONLY on capped (free) plans. When
 *     `targets_limit` is null the plan is unlimited, so we omit the line
 *     entirely rather than print a confusing "3 / ∞".
 */

import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type { ResumeUsage } from "../../api/client";

// coral via theme.palette.primary — per-product accent deferred (spec D2)
export default function UsageMeter({ usage }: { usage: ResumeUsage }) {
  const theme = useTheme();
  const { t } = useTranslation("resume");
  const { runs_used, runs_limit, targets_used, targets_limit } = usage;

  // Guard the division: a zero/absent limit shouldn't NaN the bar.
  const runsPct =
    runs_limit > 0 ? Math.min(100, (runs_used / runs_limit) * 100) : 0;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.primary.light, 0.18)}`,
        bgcolor: alpha(theme.palette.primary.light, 0.03),
      }}
    >
      <Stack spacing={1.25}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          spacing={2}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: theme.palette.primary.main,
            }}
          >
            {t("UsageMeter.runsThisMonth")}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
            {runs_used} / {runs_limit}
          </Typography>
        </Stack>

        <LinearProgress
          variant="determinate"
          value={runsPct}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: alpha(theme.palette.primary.light, 0.12),
            "& .MuiLinearProgress-bar": {
              borderRadius: 3,
              bgcolor: theme.palette.primary.light,
            },
          }}
        />

        {targets_limit !== null && (
          <Typography variant="caption" color="text.secondary">
            {t("UsageMeter.targetsLabel")}{" "}
            <Box component="span" sx={{ fontWeight: 500, color: "text.primary" }}>
              {targets_used} / {targets_limit}
            </Box>
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
